import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { apiFetch, API_BASE_URL } from './utils/api';
import { previewDocumentOutput } from './utils/documentGeneration';

const FALLBACK_LIBRARIES = [
  { key: 'invoice', label: 'Invoices', marker: 'INV' },
  { key: 'quote', label: 'Quotes', marker: 'QTE' },
  { key: 'ppm_quote', label: 'PPM Quotes', marker: 'PPMQ' },
  { key: 'job_sheet', label: 'Job Sheets', marker: 'JOB' },
  { key: 'purchase_order', label: 'Purchase Orders', marker: 'PO' },
  { key: 'risk_assessment', label: 'Risk Assessments', marker: 'RAMS' },
  { key: 'service_report', label: 'Service Reports', marker: 'SRV' },
  { key: 'completion_certificate', label: 'Completion Certificates', marker: 'CERT' },
  { key: 'delivery_note', label: 'Delivery Notes', marker: 'DN' },
  { key: 'sales_order', label: 'Sales Orders', marker: 'SO' },
  { key: 'ppm_contract', label: 'PPM Contracts', marker: 'PPM' },
  { key: 'compliance_forms', label: 'Compliance Forms', marker: 'COMP' },
];

const EMPTY_TEMPLATE = {
  name: '',
  document_type: 'invoice',
  description: '',
  is_default: false,
};

function getTypeMeta(type, libraries = FALLBACK_LIBRARIES) {
  return libraries.find((item) => item.key === type || item.value === type) || {
    key: type || 'other',
    label: type ? type.replace(/_/g, ' ') : 'Other',
    marker: 'DOC',
  };
}

function authHeaders() {
  const token = localStorage.getItem('doorbase_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function hashFile(file) {
  if (!file || !window.crypto?.subtle) return null;
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function LibraryIcon({ marker = 'DOC', active = false, small = false }) {
  const sizeClass = small ? 'w-8 h-8 text-[10px]' : 'w-11 h-11 text-xs';
  const colorClass = active
    ? 'bg-white text-[#082547]'
    : 'bg-[#082547] text-white';

  return (
    <span className={`${sizeClass} ${colorClass} rounded-lg flex items-center justify-center font-bold shrink-0`}>
      {marker || 'DOC'}
    </span>
  );
}

function ActionButton({ children, onClick, tone = 'neutral', disabled = false }) {
  const toneClass = {
    neutral: 'bg-white text-gray-700 border-gray-200 hover:border-[#082547]/30 hover:text-[#082547] hover:bg-blue-50',
    primary: 'bg-[#082547] text-white border-[#082547] hover:bg-[#0a2d5c]',
    accent: 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600',
    danger: 'bg-white text-red-600 border-red-100 hover:bg-red-50 hover:border-red-200',
  }[tone] || 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-9 items-center justify-center rounded-lg border px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${toneClass}`}
    >
      {children}
    </button>
  );
}

function buildPreviewData(documentType = 'document') {
  const today = new Date().toLocaleDateString('en-GB');
  const lineItems = [
    { description: 'Automatic door service call', quantity: 1, unit_price: '125.00', total: '125.00', line_total: '125.00' },
    { description: 'Replacement sensor and commissioning', quantity: 1, unit_price: '185.00', total: '185.00', line_total: '185.00' },
  ];

  const base = {
    document_number: 'PREVIEW-001',
    date: today,
    customer_name: 'Preview Customer Ltd',
    customer_company: 'Preview Customer Ltd',
    customer_email: 'accounts@example.com',
    customer_phone: '01234 567890',
    customer_address: '1 Example Street, London, EC1A 1AA',
    client_name: 'Preview Customer Ltd',
    client_company: 'Preview Customer Ltd',
    client_email: 'accounts@example.com',
    client_phone: '01234 567890',
    client_address: '1 Example Street, London, EC1A 1AA',
    site_address: 'Preview Site, 10 High Street, London',
    company_name: 'Automatic Door Group',
    company_address: 'Company Address',
    company_phone: '01234 000000',
    company_email: 'info@example.com',
    company_vat_number: 'GB 000 0000 00',
    subtotal: '310.00',
    vat_rate: '20',
    vat_amount: '62.00',
    total: '372.00',
    payment_terms: 'Net 30',
    bank_details: 'Bank: Preview Bank\nSort Code: 00-00-00\nAccount Number: 00000000',
    terms: 'Preview terms and conditions.',
    notes: 'Preview document generated from the template library.',
    items: lineItems,
    invoice_items: lineItems,
    quote_items: lineItems,
    po_items: lineItems,
  };

  return {
    ...base,
    invoice_number: documentType === 'invoice' ? 'INV-PREVIEW-001' : 'PREVIEW-001',
    invoice_date: today,
    due_date: today,
    quote_number: ['quote', 'ppm_quote'].includes(documentType) ? 'QTE-PREVIEW-001' : 'PREVIEW-001',
    quote_date: today,
    valid_until: today,
    job_reference: 'JOB-PREVIEW-001',
    job_number: 'JOB-PREVIEW-001',
    job_description: 'Preview job description',
    job_address: 'Preview Site, 10 High Street, London',
    job_status: 'Scheduled',
    job_created: today,
    job_scheduled: today,
    job_priority: 'Normal',
    job_notes: 'Preview job notes.',
    engineer_name: 'Preview Engineer',
    engineer_phone: '01234 111111',
    engineer_email: 'engineer@example.com',
    works_carried_out: 'Preview works carried out.',
    sales_order_number: 'SO-PREVIEW-001',
    purchase_order_number: 'PO-PREVIEW-001',
    po_number: 'PO-PREVIEW-001',
    supplier_name: 'Preview Supplier Ltd',
    supplier_contact: 'Preview Supplier Contact',
    supplier_email: 'supplier@example.com',
    supplier_phone: '01234 222222',
    supplier_address: 'Supplier Address',
    delivery_address: 'Preview Delivery Address',
    delivery_method: 'Courier',
    delivery_contact: 'Preview Site Contact',
    delivery_phone: '01234 333333',
    delivery_instructions: 'Preview delivery instructions.',
  };
}

function DocumentTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [libraryContents, setLibraryContents] = useState({});
  const [samples, setSamples] = useState([]);
  const [librariesConfig, setLibrariesConfig] = useState(FALLBACK_LIBRARIES);
  const [mergeFields, setMergeFields] = useState(null);
  const [activeLibrary, setActiveLibrary] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFields, setShowFields] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [newTemplate, setNewTemplate] = useState(EMPTY_TEMPLATE);
  const [selectedFile, setSelectedFile] = useState(null);
  const [replacementFile, setReplacementFile] = useState(null);

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const enabledLibraries = useMemo(() => {
    const enabled = librariesConfig.filter((library) => library.is_enabled !== false);
    return enabled.length > 0 ? enabled : FALLBACK_LIBRARIES;
  }, [librariesConfig]);

  const libraries = useMemo(() => {
    return templates.reduce((acc, template) => {
      const type = template.document_type || 'other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(template);
      return acc;
    }, {});
  }, [templates]);

  const typeCounts = useMemo(() => {
    return Object.entries(libraryContents).reduce((acc, [key, content]) => {
      acc[key] = content?.counts?.total || 0;
      return acc;
    }, {});
  }, [libraryContents]);

  const librarySummaries = useMemo(() => {
    return enabledLibraries.map((library) => ({
      ...library,
      count: typeCounts[library.key] || 0,
    }));
  }, [enabledLibraries, typeCounts]);

  const activeMeta = activeLibrary
    ? { ...getTypeMeta(activeLibrary, librariesConfig), count: typeCounts[activeLibrary] || 0 }
    : null;

  const activeContent = activeLibrary ? libraryContents[activeLibrary] : null;

  const filteredTemplates = useMemo(() => {
    if (!activeLibrary) return [];
    const query = searchQuery.trim().toLowerCase();
    const source = activeContent?.items || libraries[activeLibrary] || [];
    if (!query) return source;
    return source.filter((item) => {
      return [item.name, item.description, item.original_filename, item.filename, item.form_key]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));
    });
  }, [activeLibrary, activeContent, libraries, searchQuery]);

  const totalFields = mergeFields
    ? Object.values(mergeFields).reduce((sum, fields) => sum + (Array.isArray(fields) ? fields.length : 0), 0)
    : 0;

  const loadLibraries = useCallback(async function loadLibraries() {
    try {
      const res = await apiFetch('/api/documents/libraries');
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error('Unexpected library response');
      setLibrariesConfig(data);
    } catch {
      setLibrariesConfig(FALLBACK_LIBRARIES);
    }
  }, []);

  const loadTemplates = useCallback(async function loadTemplates() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/documents/libraries/contents?enabled=true');
      const data = await res.json();
      if (!data || !Array.isArray(data.libraries) || !data.contents) {
        throw new Error('Template libraries returned an unexpected response.');
      }
      setLibrariesConfig(data.libraries);
      setLibraryContents(data.contents);
      setTemplates(Object.values(data.contents).flatMap((content) => content.word_templates || []));
    } catch (err) {
      await loadLibraries();
      setTemplates([]);
      setLibraryContents({});
      setError(err.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }, [loadLibraries]);

  const loadSamples = useCallback(async function loadSamples() {
    try {
      const res = await apiFetch('/api/documents/templates/samples');
      const data = await res.json();
      setSamples(Array.isArray(data) ? data : []);
    } catch {
      setSamples([]);
    }
  }, []);

  const loadMergeFields = useCallback(async function loadMergeFields() {
    try {
      const res = await apiFetch('/api/documents/merge-fields');
      const data = await res.json();
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const cleaned = {};
        for (const [key, fields] of Object.entries(data)) {
          if (Array.isArray(fields)) cleaned[key] = fields;
        }
        setMergeFields(cleaned);
      }
    } catch {
      setMergeFields(null);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadSamples();
    loadMergeFields();
  }, [loadTemplates, loadSamples, loadMergeFields]);

  function openUpload(type = activeLibrary) {
    const documentType = type || enabledLibraries[0]?.key || 'invoice';
    setNewTemplate({ ...EMPTY_TEMPLATE, document_type: documentType });
    setSelectedFile(null);
    setShowUpload(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function copyField(field) {
    const text = `{{${field}}}`;
    const markCopied = () => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => fallbackCopy(text, markCopied));
      return;
    }
    fallbackCopy(text, markCopied);
  }

  function fallbackCopy(text, onCopied) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      if (onCopied) onCopied();
    } catch {
      setError('Could not copy field to clipboard.');
    }
    document.body.removeChild(textarea);
  }

  function handleDownloadSample(filename) {
    window.open(`${API_BASE_URL}/api/documents/templates/samples/${filename}`, '_blank');
  }

  async function handleSeed() {
    setSeeding(true);
    setError(null);
    try {
      const res = await apiFetch('/api/documents/templates/seed', { method: 'POST' });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Seed failed.');
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to add sample templates.');
    } finally {
      setSeeding(false);
    }
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    try {
      const originalHash = await hashFile(selectedFile);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', newTemplate.name);
      formData.append('document_type', newTemplate.document_type);
      formData.append('description', newTemplate.description);
      formData.append('is_default', newTemplate.is_default ? 'true' : 'false');

      const res = await fetch(`${API_BASE_URL}/api/documents/templates`, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Upload failed.');
      if (originalHash && data.file_sha256 && originalHash !== data.file_sha256) {
        throw new Error(`Upload integrity mismatch. Original ${originalHash.slice(0, 12)}, stored ${data.file_sha256.slice(0, 12)}.`);
      }

      setShowUpload(false);
      setNewTemplate(EMPTY_TEMPLATE);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to upload template.');
    } finally {
      setUploading(false);
    }
  }

  async function handleReplace(event) {
    event.preventDefault();
    if (!replaceTarget || !replacementFile) return;

    setUploading(true);
    setError(null);
    try {
      const originalHash = await hashFile(replacementFile);
      const formData = new FormData();
      formData.append('file', replacementFile);
      const replaceUrl = replaceTarget.type === 'pdf_template'
        ? `${API_BASE_URL}/api/documents/pdf-templates/${replaceTarget.id}/replace`
        : `${API_BASE_URL}/api/documents/templates/${replaceTarget.id}/replace`;

      const res = await fetch(replaceUrl, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Replace failed.');
      if (originalHash && data.file_sha256 && originalHash !== data.file_sha256) {
        throw new Error(`Replace integrity mismatch. Original ${originalHash.slice(0, 12)}, stored ${data.file_sha256.slice(0, 12)}.`);
      }

      setReplaceTarget(null);
      setReplacementFile(null);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to replace template file.');
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(item) {
    setError(null);
    try {
      const deleteUrl = item.type === 'pdf_template'
        ? `/api/documents/pdf-templates/${item.id}`
        : item.type === 'form_template'
          ? `/api/documents/forms/${item.id}`
          : `/api/documents/templates/${item.id}`;
      const res = await apiFetch(deleteUrl, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed.');
      setDeleteConfirm(null);
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to delete template.');
    }
  }

  async function handleSetDefault(template) {
    setError(null);
    try {
      const defaultUrl = template.type === 'pdf_template'
        ? `/api/documents/pdf-templates/${template.id}`
        : `/api/documents/templates/${template.id}`;
      const res = await apiFetch(defaultUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: template.name,
          description: template.description,
          is_default: true,
          document_type: template.document_type,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to set default.');
      await loadTemplates();
    } catch (err) {
      setError(err.message || 'Failed to set default template.');
    }
  }

  async function handleDownload(template) {
    setError(null);
    try {
      const downloadUrl = template.type === 'pdf_template'
        ? `${API_BASE_URL}/api/documents/pdf-templates/${template.id}/file`
        : `${API_BASE_URL}/api/documents/templates/${template.id}/download`;
      const res = await fetch(downloadUrl, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Download failed.');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.original_filename || template.filename || `${template.name}.${template.type === 'pdf_template' ? 'pdf' : 'docx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download template.');
    }
  }

  async function handlePreview(template) {
    setError(null);
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
      previewWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing template preview...</p>');
    }

    try {
      await previewDocumentOutput(
        template,
        buildPreviewData(template.document_type),
        `${template.name || 'Template'}-Preview`,
        previewWindow
      );
    } catch (err) {
      if (previewWindow && !previewWindow.closed) {
        previewWindow.close();
      }
      setError(err.message || 'Failed to preview template.');
    }
  }

  function startReplace(item) {
    setReplaceTarget(item);
    setReplacementFile(null);
    if (replaceInputRef.current) replaceInputRef.current.value = '';
  }

  function renderLibraryActions(item) {
    if (item.type === 'form_template') {
      return (
        <>
          <ActionButton onClick={() => navigate(`/design-hub/form-designer?form=${encodeURIComponent(item.id)}`)}>Edit</ActionButton>
          <ActionButton onClick={() => setDeleteConfirm(item)} tone="danger">Delete</ActionButton>
        </>
      );
    }

    if (item.type === 'pdf_template') {
      return (
        <>
          <ActionButton onClick={() => navigate(`/design-hub/pdf-templates?type=${encodeURIComponent(item.document_type || 'invoice')}&template=${encodeURIComponent(item.id)}`)}>Edit</ActionButton>
          <ActionButton onClick={() => handleDownload(item)}>Download</ActionButton>
          <ActionButton onClick={() => startReplace(item)}>Replace</ActionButton>
          {!item.is_default && <ActionButton onClick={() => handleSetDefault(item)}>Make default</ActionButton>}
          <ActionButton onClick={() => setDeleteConfirm(item)} tone="danger">Delete</ActionButton>
        </>
      );
    }

    return (
      <>
        {!item.is_default && <ActionButton onClick={() => handleSetDefault(item)}>Make default</ActionButton>}
        <ActionButton onClick={() => handleDownload(item)} disabled={item.file_stored !== true}>Download</ActionButton>
        <ActionButton onClick={() => handlePreview(item)} disabled={item.file_stored !== true}>Preview</ActionButton>
        <ActionButton onClick={() => startReplace(item)}>Replace</ActionButton>
        <ActionButton onClick={() => setDeleteConfirm(item)} tone="danger">Delete</ActionButton>
      </>
    );
  }

  function renderSampleSection(compact = false) {
    if (samples.length === 0) return null;

    return (
      <section className={`bg-white rounded-lg border border-gray-200 ${compact ? 'p-4' : 'p-5'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Sample Templates</h2>
            <p className="text-sm text-gray-700 mt-1">Download a sample, customise it in Word, then upload it back into the right library.</p>
          </div>
          <ActionButton onClick={handleSeed} disabled={seeding} tone="primary">
            {seeding ? 'Adding samples...' : 'Add Samples to Library'}
          </ActionButton>
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {samples.map((sample) => {
            const type = getTypeMeta(sample.document_type, librariesConfig);
            const alreadyHas = templates.some((template) => template.document_type === sample.document_type);
            return (
              <button
                key={sample.filename}
                onClick={() => handleDownloadSample(sample.filename)}
                className={`flex items-center gap-3 border rounded-lg px-3 py-2 text-left text-sm transition ${
                  alreadyHas ? 'bg-gray-50 border-gray-200 text-gray-500' : 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
                }`}
              >
                <LibraryIcon marker={type.marker} small />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{sample.name}</span>
                  <span className="block text-xs opacity-70">{alreadyHas ? 'Library has template' : 'Sample .docx'}</span>
                </span>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  function renderTemplateRow(template) {
    const type = getTypeMeta(template.document_type, librariesConfig);
    if (template.type === 'pdf_template') {
      return (
        <article key={`pdf-${template.id}`} className="grid gap-3 px-4 py-3 transition hover:bg-blue-50/50 lg:grid-cols-[minmax(0,1fr)_180px_220px] lg:items-center">
          <div className="flex items-start gap-3 min-w-0">
            <LibraryIcon marker={type.marker} small />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-700">PDF overlay</span>
                {template.is_default && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-100 text-orange-700">Default</span>
                )}
              </div>
              {template.description && (
                <p className="text-sm text-gray-500 mt-1 truncate">{template.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-1 truncate">{template.original_filename || template.filename || 'PDF template'}</p>
            </div>
          </div>
          <div className="text-xs text-gray-400 lg:text-right">
            {template.file_sha256 ? <span className="font-mono">File ID {template.file_sha256.slice(0, 12)}</span> : <span>PDF overlay</span>}
          </div>
          <div className="flex flex-wrap justify-start lg:justify-end gap-2">
            {renderLibraryActions(template)}
          </div>
        </article>
      );
    }

    if (template.type === 'form_template') {
      return (
        <article key={`form-${template.id}`} className="grid gap-3 px-4 py-3 transition hover:bg-blue-50/50 lg:grid-cols-[minmax(0,1fr)_180px_220px] lg:items-center">
          <div className="flex items-start gap-3 min-w-0">
            <LibraryIcon marker={type.marker} small />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700">Form questions</span>
                {template.is_enabled === false && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500">Disabled</span>
                )}
              </div>
              {template.description && (
                <p className="text-sm text-gray-500 mt-1 truncate">{template.description}</p>
              )}
              <p className="text-xs text-gray-400 mt-1 truncate">
                {template.form_key || 'Form template'}{Number.isFinite(template.question_count) ? ` · ${template.question_count} questions` : ''}
              </p>
            </div>
          </div>
          <div className="text-xs text-gray-400 lg:text-right">
            <span>{Number.isFinite(template.question_count) ? `${template.question_count} fields` : 'Form fields'}</span>
          </div>
          <div className="flex flex-wrap justify-start lg:justify-end gap-2">
            {renderLibraryActions(template)}
          </div>
        </article>
      );
    }

    const stored = template.file_stored === true;
    return (
      <article key={template.id} className="grid gap-3 px-4 py-3 transition hover:bg-blue-50/50 lg:grid-cols-[minmax(0,1fr)_180px_340px] lg:items-center">
        <div className="flex items-start gap-3 min-w-0">
          <LibraryIcon marker={type.marker} small />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
              {template.is_default && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                  Default
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-0.5 rounded ${stored ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                {stored ? 'Stored safely' : 'Record only'}
              </span>
            </div>
            {template.description && (
              <p className="text-sm text-gray-500 mt-1 truncate">{template.description}</p>
            )}
            <p className="text-xs text-gray-400 mt-1 truncate">{template.original_filename || template.filename}</p>
            {!stored && (
              <p className="text-xs text-yellow-700 mt-1">Replace this template once to store the Word file safely.</p>
            )}
          </div>
        </div>

        <div className="text-xs text-gray-400 lg:text-right">
          {template.file_sha256 ? (
            <span className="font-mono">File ID {template.file_sha256.slice(0, 12)}</span>
          ) : (
            <span>No file ID</span>
          )}
        </div>

        <div className="flex flex-wrap justify-start lg:justify-end gap-2">
          {renderLibraryActions(template)}
        </div>
      </article>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Document Templates"
        subtitle="Document libraries organised from setup"
        backTo="/design-hub"
        backLabel="Design Hub"
        actionButton={
          <button onClick={() => openUpload()} className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
            Upload Template
          </button>
        }
        sticky
      />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center justify-between gap-4">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-semibold">Close</button>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <section className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Libraries</h2>
                <p className="mt-1 text-xs text-gray-400">{librarySummaries.length} enabled libraries from setup</p>
              </div>
              <div className="space-y-1">
                {librarySummaries.map(({ key, label, marker, count }) => {
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setActiveLibrary(key);
                        setSearchQuery('');
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left transition flex items-center gap-3 ${
                        activeLibrary === key ? 'bg-[#082547] text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <LibraryIcon marker={marker} active={activeLibrary === key} small />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{label}</span>
                        <span className={`block text-xs ${activeLibrary === key ? 'text-blue-100' : 'text-gray-400'}`}>{count} items</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {!loading && renderSampleSection(true)}
          </aside>

          <section className="min-w-0 space-y-4">
            {!activeLibrary ? (
              <div className="min-h-[360px] rounded-lg border border-dashed border-gray-300 bg-white/60" />
            ) : loading ? (
              <div className="text-center py-20 text-gray-400 bg-white border border-gray-200 rounded-lg">Loading templates...</div>
            ) : activeMeta.count === 0 ? (
              <>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <LibraryIcon marker={activeMeta.marker} />
                      <div className="min-w-0">
                        <h2 className="text-xl font-semibold text-gray-900 truncate">{activeMeta.label}</h2>
                        <p className="text-sm text-gray-500 mt-1">No Word, PDF overlay, or form items have been added to this library yet.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href="/word-addin/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 items-center justify-center rounded-lg border border-[#082547] bg-[#082547] px-3 text-xs font-semibold text-white transition hover:bg-[#0a2d5c]"
                      >
                        Word Add-in
                      </a>
                      <ActionButton onClick={() => openUpload(activeLibrary)} tone="accent">Upload Template</ActionButton>
                      <ActionButton onClick={() => setShowFields(!showFields)}>
                        {showFields ? 'Hide Merge Fields' : 'Merge Fields'}
                      </ActionButton>
                    </div>
                  </div>
                </div>
                <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
                  <div className="flex justify-center mb-3">
                    <LibraryIcon marker={activeMeta.marker} />
                  </div>
                  <h2 className="text-lg font-medium text-gray-800 mb-2">Empty {activeMeta.label} library</h2>
                  <p className="text-gray-500 mb-4">Upload a Word template here, or add PDF overlays and form templates from their Design Hub tools.</p>
                  <ActionButton onClick={() => openUpload(activeLibrary)} tone="accent">Upload Template</ActionButton>
                </div>
              </>
            ) : filteredTemplates.length === 0 ? (
              <>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <LibraryIcon marker={activeMeta.marker} />
                      <div className="min-w-0">
                        <h2 className="text-xl font-semibold text-gray-900 truncate">{activeMeta.label}</h2>
                        <p className="text-sm text-gray-500 mt-1">{activeMeta.count} library items available.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <ActionButton onClick={() => openUpload(activeLibrary)} tone="accent">Upload Template</ActionButton>
                      <ActionButton onClick={() => setShowFields(!showFields)}>
                        {showFields ? 'Hide Merge Fields' : 'Merge Fields'}
                      </ActionButton>
                    </div>
                  </div>
                  <div className="mt-4">
                    <input
                      type="text"
                      placeholder="Search by name, notes, filename, or form key"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      className="w-full sm:max-w-lg px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                    />
                  </div>
                </div>
                <div className="text-center py-10 bg-white border border-gray-200 rounded-lg">
                  <h2 className="text-base font-medium text-gray-800 mb-2">No matching library items</h2>
                  <p className="text-sm text-gray-500">Clear the search to see every item in {activeMeta.label}.</p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <LibraryIcon marker={activeMeta.marker} />
                      <div className="min-w-0">
                        <h2 className="text-xl font-semibold text-gray-900 truncate">{activeMeta.label}</h2>
                        <p className="text-sm text-gray-500 mt-1">
                          {activeMeta.count} library items available across Word templates, PDF overlays, and form templates.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href="/word-addin/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 items-center justify-center rounded-lg border border-[#082547] bg-[#082547] px-3 text-xs font-semibold text-white transition hover:bg-[#0a2d5c]"
                      >
                        Word Add-in
                      </a>
                      <ActionButton onClick={() => openUpload(activeLibrary)} tone="accent">Upload Template</ActionButton>
                      <ActionButton onClick={() => setShowFields(!showFields)}>
                        {showFields ? 'Hide Merge Fields' : 'Merge Fields'}
                      </ActionButton>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <input
                      type="text"
                      placeholder="Search by name, notes, filename, or form key"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      className="w-full sm:max-w-lg px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                    />
                    <ActionButton onClick={() => openUpload(activeLibrary)} tone="primary">
                      Add to {activeMeta.label}
                    </ActionButton>
                  </div>
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <div className="hidden border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 lg:grid lg:grid-cols-[minmax(0,1fr)_180px_340px]">
                    <span>Library Item</span>
                    <span className="text-right">Details</span>
                    <span className="text-right">Actions</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {filteredTemplates.map((template) => renderTemplateRow(template))}
                  </div>
                </div>
              </>
            )}
            {activeLibrary && filteredTemplates.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-gray-900">Merge Fields</h2>
                    <p className="text-sm text-gray-500 mt-1">Copy fields for templates in this library.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {mergeFields && !showFields && (
                      <span className="text-xs text-gray-400">{totalFields} fields across {Object.keys(mergeFields).length} areas</span>
                    )}
                    <button onClick={() => setShowFields(!showFields)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium">
                      {showFields ? 'Hide Fields' : 'Show Fields'}
                    </button>
                    <button
                      onClick={() => window.open(`${API_BASE_URL}/api/documents/merge-fields-reference.docx`, '_blank')}
                      className="text-xs bg-[#082547] hover:bg-[#0a2d5c] text-white px-3 py-1.5 rounded-lg font-medium"
                    >
                      Reference DOCX
                    </button>
                  </div>
                </div>
                {showFields && mergeFields && (
                  <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
                    {Object.entries(mergeFields).filter(([key]) => key !== 'custom').map(([category, fields]) => (
                      <div key={category}>
                        <h3 className="font-medium text-gray-700 mb-2 capitalize">
                          {category.replace(/_/g, ' ')}
                        </h3>
                        <div className="space-y-1">
                          {Array.isArray(fields) && fields.map((item) => {
                            if (item.type === 'loop') {
                              return (
                                <div key={item.field} className="space-y-1">
                                  <button onClick={() => copyField(`FOR item IN ${item.field}`)} className="inline-flex items-center gap-1 bg-gray-50 hover:bg-orange-50 border border-gray-200 rounded px-2 py-0.5 font-mono text-xs transition">
                                    {`{{FOR item IN ${item.field}}}`} <span className="text-orange-500">{copiedField === `FOR item IN ${item.field}` ? 'copied' : 'copy'}</span>
                                  </button>
                                  <div className="ml-4 space-y-1">
                                    {item.children?.map((child) => (
                                      <button key={child.field} onClick={() => copyField(`$item.${child.field}`)} className="inline-flex items-center gap-1 bg-gray-50 hover:bg-orange-50 border border-gray-200 rounded px-2 py-0.5 font-mono text-xs transition">
                                        {`{{$item.${child.field}}}`} <span className="text-orange-500">{copiedField === `$item.${child.field}` ? 'copied' : 'copy'}</span>
                                      </button>
                                    ))}
                                    <button onClick={() => copyField('END-FOR item')} className="inline-flex items-center gap-1 bg-gray-50 hover:bg-orange-50 border border-gray-200 rounded px-2 py-0.5 font-mono text-xs transition">
                                      {'{{END-FOR item}}'}
                                    </button>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <button key={item.field} onClick={() => copyField(item.field)} className="inline-flex items-center gap-1.5 w-full text-left group">
                                <code className="bg-gray-50 group-hover:bg-orange-50 border border-transparent group-hover:border-orange-200 rounded px-2 py-0.5 font-mono text-xs transition flex-1">
                                  {`{{${item.field}}}`}
                                </code>
                                <span className="text-gray-400 group-hover:text-orange-500 text-xs">
                                  {copiedField === item.field ? 'copied' : 'copy'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>

      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUpload(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg mx-4" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Word Template</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  required
                  value={newTemplate.name}
                  onChange={(event) => setNewTemplate((prev) => ({ ...prev, name: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                  placeholder="ADG invoice template"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Library</label>
                <select
                  value={newTemplate.document_type}
                  onChange={(event) => setNewTemplate((prev) => ({ ...prev, document_type: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                >
                  {enabledLibraries.map(({ key, label, marker }) => (
                    <option key={key} value={key}>{marker} - {label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={newTemplate.description}
                  onChange={(event) => setNewTemplate((prev) => ({ ...prev, description: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#082547]/20"
                  placeholder="Optional notes for admins"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={newTemplate.is_default}
                  onChange={(event) => setNewTemplate((prev) => ({ ...prev, is_default: event.target.checked }))}
                  className="rounded border-gray-300"
                />
                Set as default for this library
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Word Template (.docx)</label>
                <input
                  type="file"
                  accept=".docx,.docm"
                  required
                  ref={fileInputRef}
                  onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#082547] file:text-white hover:file:bg-[#0a2d5c]"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={uploading || !selectedFile} className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium">
                  {uploading ? 'Uploading...' : 'Upload Template'}
                </button>
                <button type="button" onClick={() => setShowUpload(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {replaceTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setReplaceTarget(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Replace {replaceTarget.type === 'pdf_template' ? 'PDF Template' : 'Template File'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">{replaceTarget.name}</p>
            <form onSubmit={handleReplace} className="space-y-4">
              <input
                type="file"
                accept={replaceTarget.type === 'pdf_template' ? '.pdf,application/pdf' : '.docx,.docm'}
                required
                ref={replaceInputRef}
                onChange={(event) => setReplacementFile(event.target.files?.[0] || null)}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-[#082547] file:text-white hover:file:bg-[#0a2d5c]"
              />
              <div className="flex gap-3">
                <button type="submit" disabled={uploading || !replacementFile} className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium">
                  {uploading ? 'Replacing...' : 'Replace File'}
                </button>
                <button type="button" onClick={() => setReplaceTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4" onClick={(event) => event.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Delete Library Item?</h2>
            <p className="text-sm text-gray-500 mb-4">
              This removes {deleteConfirm.name || 'this item'} from the library. It cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium">
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentTemplatesPage;
